import { useDatabaseContent } from '@databases/database-state';
import {
  BasicDatabase,
  BrokenDatabase,
  EntryCreateOptions,
  EntryWithContext,
} from '@databases/types/database';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import EntryEmptyState from '../../assets/entry_empty_state.svg';
import searchError from '../../assets/search_error.svg';
import searchIllustration from '../../assets/search_illustration.svg';
import { messages } from '../../i18n';
import {
  EmbedReferenceContainer,
  PlaceholderContainer,
  PreviewContainer,
  PreviewPlaceholderHeaderText,
  PreviewPlaceholderText,
  ValueContainer,
} from '../embedReference/embedReference.styles';
import { isCreate } from '../entryPicker/DatabaseEntryPicker';

export type DatabasePreviewComponentProps = {
  contentId: string;
  canEditContent: boolean;
  couldEditFields: boolean;
  canEditFields: boolean;
  isContentEditable?: boolean;
  outerContainerRef: HTMLElement | null;
  setOuterContainerRef: (ref: HTMLElement | null) => void;
  hasSelection: boolean;
};

export const DatabasePlaceholder: FunctionComponent<{ error?: boolean }> = ({
  error = false,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <img
        width={108}
        height={108}
        role="presentation"
        src={error ? searchError : searchIllustration}
      />
      {error && (
        <PreviewPlaceholderHeaderText>
          {formatMessage(messages['reference-preview.broken-header'])}
        </PreviewPlaceholderHeaderText>
      )}
      <PreviewPlaceholderText>
        {formatMessage(
          messages[
            error
              ? 'reference-preview.broken-description'
              : 'reference-preview.placeholder'
          ]
        )}
      </PreviewPlaceholderText>
    </>
  );
};

export const EntryPlaceHolder: FunctionComponent = () => {
  const { formatMessage } = useIntl();

  return (
    <>
      <img
        width={242}
        height={111}
        role="presentation"
        src={EntryEmptyState}
        alt={formatMessage(messages['reference-preview.entry-placeholder-header'])}
      />
      <PreviewPlaceholderHeaderText>
        {formatMessage(messages['reference-preview.entry-placeholder-header'])}
      </PreviewPlaceholderHeaderText>
      <PreviewPlaceholderText>
        {formatMessage(messages['reference-preview.entry-placeholder-description'])}
      </PreviewPlaceholderText>
    </>
  );
};

const DatabaseComponentContainer = ({
  contentId,
  DatabasePreviewComponent,
}: {
  contentId: string;
  DatabasePreviewComponent: FunctionComponent<DatabasePreviewComponentProps>;
}) => {
  const { setContentId } = useDatabaseContent();

  useEffect(() => {
    setContentId(contentId);
  }, [contentId, setContentId]);

  const [outerContainerRef, setOuterContainerRef] = useState<HTMLElement | null>(null);

  return (
    <DatabasePreviewComponent
      contentId={contentId}
      isContentEditable={false}
      canEditContent={false}
      couldEditFields={false}
      canEditFields={false}
      outerContainerRef={outerContainerRef}
      setOuterContainerRef={setOuterContainerRef}
      hasSelection={false}
    />
  );
};

export interface DatabasePreviewProps {
  databaseError: boolean;
  selectedDatabase: BasicDatabase | BrokenDatabase | undefined;
  selectedEntry?: EntryWithContext | EntryCreateOptions | undefined;
  selectedField?: string | undefined;
  DatabasePreviewComponent: FunctionComponent<DatabasePreviewComponentProps>;
  EntryPreviewComponent: FunctionComponent<{
    contentId: string;
    entryId: string;
    isContentEditable?: boolean;
  }>;
  ValuePreviewComponent: FunctionComponent<{
    contentId: string;
    entryId: string;
    fieldId: string;
  }>;
}

export const DatabasePreview: FunctionComponent<DatabasePreviewProps> = ({
  databaseError,
  selectedDatabase,
  DatabasePreviewComponent,
  EntryPreviewComponent,
  ValuePreviewComponent,
  selectedEntry,
  selectedField,
}) => {
  if (databaseError) {
    return (
      <PlaceholderContainer>
        <DatabasePlaceholder error={true} />
      </PlaceholderContainer>
    );
  }
  if (selectedField && selectedEntry && 'entry' in selectedEntry && selectedDatabase) {
    return (
      <ValueContainer>
        <ValuePreviewComponent
          contentId={selectedDatabase.contentId}
          entryId={selectedEntry.entry.id}
          fieldId={selectedField}
        />
      </ValueContainer>
    );
  }
  if (selectedEntry && isCreate(selectedEntry)) {
    return (
      <EmbedReferenceContainer>
        <PlaceholderContainer>
          <EntryPlaceHolder />
        </PlaceholderContainer>
      </EmbedReferenceContainer>
    );
  }

  if (selectedEntry && 'entry' in selectedEntry && selectedDatabase) {
    return (
      <PreviewContainer>
        <EntryPreviewComponent
          contentId={selectedDatabase.contentId}
          entryId={selectedEntry.entry.id}
          isContentEditable={false}
        />
      </PreviewContainer>
    );
  }
  if (selectedDatabase) {
    return (
      <DatabaseComponentContainer
        contentId={selectedDatabase.contentId}
        DatabasePreviewComponent={DatabasePreviewComponent}
      />
    );
  }

  return (
    <EmbedReferenceContainer>
      <PlaceholderContainer>
        <DatabasePlaceholder />
      </PlaceholderContainer>
    </EmbedReferenceContainer>
  );
};
